import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiEdit3 } from 'react-icons/fi';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

/**
 * Edição do card da trilha. Somente name/description/active/position —
 * slug, kind e scope_config são imutáveis pela UI (regra do spec).
 */
export default function EditTrilhaModal({ trilha, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [position, setPosition] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!trilha) return;
    setName(trilha.name || '');
    setDescription(trilha.description || '');
    setActive(Boolean(trilha.active));
    setPosition(trilha.position ?? '');
  }, [trilha]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('O nome da trilha é obrigatório.');
      return;
    }
    setSubmitting(true);
    try {
      await adminLearningPathsApi.update(trilha.id, {
        name: name.trim(),
        description: description.trim(),
        active,
        position: position === '' ? null : Number(position)
      });
      toast.success('Trilha atualizada.');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.errors?.[0]?.title || 'Falha ao salvar a trilha.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={Boolean(trilha)}
      onOpenChange={isOpen => {
        if (!isOpen && !submitting) onClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary-dark">
            <FiEdit3 /> Editar trilha
          </DialogTitle>
          <DialogDescription>Nome e descrição aparecem no card do aluno. Desativar esconde a trilha do catálogo (trilhas já iniciadas não são afetadas).</DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <div>
            <Label htmlFor="trilha-name">Nome</Label>
            <Input
              id="trilha-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex.: SAEB 5º ano EF"
            />
          </div>

          <div>
            <Label htmlFor="trilha-description">Descrição do card</Label>
            <Textarea
              id="trilha-description"
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Texto curto exibido no card da trilha para o aluno."
            />
          </div>

          <div className="grid grid-cols-2 items-end gap-3">
            <div>
              <Label htmlFor="trilha-position">Posição (ordem dos cards)</Label>
              <Input
                id="trilha-position"
                type="number"
                min={0}
                value={position}
                onChange={e => setPosition(e.target.value)}
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm">
              <Switch
                checked={active}
                onCheckedChange={setActive}
              />
              {active ? 'Ativa (visível no catálogo)' : 'Inativa (oculta do catálogo)'}
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
            >
              {submitting ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
